// ---------------------------------------------------------------------------
// Admin session.
//
// Credentials are exchanged for a Supabase JWT by the API; the token is kept in
// localStorage and attached to every request by the api client.
// ---------------------------------------------------------------------------

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { api, setAccessToken } from '../lib/apiClient.js';

const AuthContext = createContext(null);

const SESSION_KEY = 'connix-admin-session';

const readSession = () => {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY) ?? 'null');
  } catch {
    return null;
  }
};

export function AuthProvider({ children }) {
  const [session, setSession] = useState(readSession);
  const [user, setUser] = useState(null);
  const [isRestoring, setIsRestoring] = useState(Boolean(readSession()));

  // Make the stored token available to the api client before anything fetches.
  useEffect(() => {
    setAccessToken(session?.accessToken ?? null);
  }, [session]);

  const signOut = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
    setAccessToken(null);
    setSession(null);
    setUser(null);
  }, []);

  // Restore on load: verify the token, and try the refresh token if it expired.
  useEffect(() => {
    const stored = readSession();
    if (!stored) {
      setIsRestoring(false);
      return;
    }

    let cancelled = false;
    setAccessToken(stored.accessToken);

    api
      .get('/auth/me')
      .then((result) => {
        if (!cancelled) setUser(result.user);
      })
      .catch(async () => {
        try {
          const refreshed = await api.post('/auth/refresh', { refreshToken: stored.refreshToken });
          if (cancelled) return;
          localStorage.setItem(SESSION_KEY, JSON.stringify(refreshed.session));
          setAccessToken(refreshed.session.accessToken);
          setSession(refreshed.session);
          setUser(refreshed.user);
        } catch {
          if (!cancelled) signOut();
        }
      })
      .finally(() => {
        if (!cancelled) setIsRestoring(false);
      });

    return () => {
      cancelled = true;
    };
  }, [signOut]);

  const signIn = useCallback(async (email, password) => {
    const result = await api.post('/auth/login', { email, password });
    localStorage.setItem(SESSION_KEY, JSON.stringify(result.session));
    setAccessToken(result.session.accessToken);
    setSession(result.session);
    setUser(result.user);
    return result.user;
  }, []);

  const value = useMemo(
    () => ({
      user,
      session,
      isRestoring,
      isAuthenticated: Boolean(user),
      isAdmin: user?.role === 'admin',
      signIn,
      signOut,
    }),
    [user, session, isRestoring, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
